import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { LucideIcon, LucideIconName } from 'utils/lucideIcon'
import './kebabMenu.scss'

export interface KebabMenuOption {
    label: string
    link?: string
    icon?: LucideIconName
    onClick?: () => void
    disabled?: boolean
    disabledReason?: string
}

interface KebabMenuProps {
    options: KebabMenuOption[]
    className?: string
    listAnchor?: 'left' | 'right'
    disabled?: boolean
    triggerIcon?: LucideIconName
    triggerLabel?: string
}

const DEFAULT_TRIGGER_LABEL = 'More actions'

export const KebabMenu = ({
    options,
    className = '',
    listAnchor = 'left',
    disabled,
    triggerIcon = 'EllipsisVertical',
    triggerLabel = DEFAULT_TRIGGER_LABEL,
}: KebabMenuProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
    const focusOnOpen = useRef<'first' | 'last' | null>(null)
    const history = useHistory()

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const menuItems = (): HTMLButtonElement[] =>
        itemRefs.current.slice(0, options.length).filter((item): item is HTMLButtonElement => !!item)

    useEffect(() => {
        if (!isOpen || !focusOnOpen.current) {
            return
        }
        const items = menuItems()
        const target = focusOnOpen.current === 'last' ? items[items.length - 1] : items[0]
        focusOnOpen.current = null
        target?.focus()
    }, [isOpen])

    const close = (returnFocus: boolean) => {
        setIsOpen(false)
        if (returnFocus) {
            triggerRef.current?.focus()
        }
    }

    const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) {
            return
        }
        if (event.key === 'Enter' || event.key === ' ') {
            focusOnOpen.current = isOpen ? null : 'first'
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()
            const where = event.key === 'ArrowUp' ? 'last' : 'first'
            if (isOpen) {
                const items = menuItems()
                const target = where === 'last' ? items[items.length - 1] : items[0]
                target?.focus()
            } else {
                focusOnOpen.current = where
                setIsOpen(true)
            }
        } else if (event.key === 'Escape' && isOpen) {
            event.preventDefault()
            event.stopPropagation()
            close(false)
        }
    }

    const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const items = menuItems()
        const index = items.indexOf(document.activeElement as HTMLButtonElement)
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            event.stopPropagation()
            items[(index + 1) % items.length]?.focus()
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()
            items[(index - 1 + items.length) % items.length]?.focus()
        } else if (event.key === 'Home') {
            event.preventDefault()
            event.stopPropagation()
            items[0]?.focus()
        } else if (event.key === 'End') {
            event.preventDefault()
            event.stopPropagation()
            items[items.length - 1]?.focus()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            close(true)
        } else if (event.key === 'Tab') {
            close(false)
        }
    }

    return (
        <div ref={menuRef} className={classNames('kebab-menu', className, { disabled })}>
            <button
                ref={triggerRef}
                type="button"
                className="kebab-menu-trigger"
                aria-label={triggerLabel}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                disabled={disabled}
                onKeyDown={onTriggerKeyDown}
                onClick={(event) => {
                    event.stopPropagation()
                    if (!disabled) {
                        setIsOpen(!isOpen)
                    }
                }}
            >
                <LucideIcon iconName={triggerIcon} strokeWidth={2} />
            </button>
            <div
                role="menu"
                aria-label={triggerLabel}
                className={classNames(`kebab-menu-options ${listAnchor}`, { open: isOpen })}
                onKeyDown={onMenuKeyDown}
            >
                {options.map((option, index) => (
                    <button
                        type="button"
                        role="menuitem"
                        tabIndex={-1}
                        aria-disabled={option.disabled}
                        ref={(element) => {
                            itemRefs.current[index] = element
                        }}
                        className={classNames('kebab-menu-option', { disabled: option.disabled })}
                        key={option.label}
                        onClick={(event) => {
                            event.stopPropagation()
                            if (option.disabled) {
                                return
                            }
                            close(true)
                            if (option.onClick) {
                                option.onClick()
                            } else if (option.link) {
                                history.push(option.link)
                            }
                        }}
                    >
                        {option.icon && <LucideIcon iconName={option.icon} className="kebab-menu-option-icon" />}
                        <span className="kebab-menu-option-text">
                            <span className="kebab-menu-option-label">{option.label}</span>
                            {option.disabled && option.disabledReason && (
                                <span className="kebab-menu-option-reason">{option.disabledReason}</span>
                            )}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    )
}
